import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext.tsx";
import {
  barristerCfaApplies,
  ConditionalFeeAgreement,
  PartyAgreements,
} from "../../shared/Agreements.ts";
import { CostType } from "../../shared/CaseConfiguration.ts";
import { getCostsTotal, isPartySettled } from "../../shared/CaseModelUtility.ts";
import { settlementCostsByType } from "../../shared/settlementCosts.ts";
import { stepsOf } from "./cfaTerms.ts";
import { ThirdPartyFundingAgreement } from "../costs/ThirdPartyFundingAgreement.tsx";
import { AfterTheEventInsuranceAgreement } from "./AfterTheEventInsuranceAgreement.tsx";
import { AgreementRow } from "./AgreementRow.tsx";
import ConditionalFeeAgreementConfigurator from "./ConditionalFeeAgreementConfigurator.tsx";
import { useTheme } from "../../context/ThemeContext";

export const AgreementsOfParty = ({
  agreements,
  onUpdate,
  onSave,
  saveOngoing,
}: {
  agreements: PartyAgreements;
  onUpdate: (newAgreements: PartyAgreements) => void;
  onSave: (newAgreements: PartyAgreements) => void;
  saveOngoing: boolean;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "agreement",
  });

  const {
    partyId,
    lawFirmCfa,
    barristerCfa,
    afterTheEventInsurance,
    thirdPartyFunding,
  } = agreements;
  const partyName = useCase().getPartyDescriptors()[partyId]?.name ?? partyId;
  const {
    budgets,
    agreements: savedAgreements,
    results,
    settlements,
    claims,
    parties,
  } = useCase().getCase();
  const saved = savedAgreements?.find((ofParty) => ofParty.partyId === partyId);
  const budget = budgets?.find((budget) => budget.partyId === partyId);
  // what the party's settlements locked: what of it is not to be spent is out
  const locked = results?.lockedCosts?.[partyId];

  /**
   * What a CFA's base holds of the party's costs of a type, as the
   * calculation charges it while the case goes on: its whole budget less the
   * stages the CFA leaves out and what settlements left not to be spent, and
   * its settlement costs.
   */
  const costsCovered = useMemo(
    () => (type: CostType, cfa: ConditionalFeeAgreement | undefined) => {
      const covers = (stage: number) =>
        !(cfa?.excludedStages ?? []).includes(stage);
      let total = 0;
      for (const costs of budget?.costs ?? []) {
        if (!covers(costs.stage)) continue;
        const all = getCostsTotal(costs).getAll();
        total += (
          type === "lawFirmFee" ? all.lawFirmFees : all.disbursements[type]
        ).toNumber();
      }
      for (const cost of locked?.costs ?? [])
        if (cost.type === type && covers(cost.stage)) total -= cost.estimated;
      for (const offer of settlements ?? []) {
        if (!covers(offer.stage)) continue;
        const side =
          offer.offerorId === partyId
            ? offer.costs.settlement.offeror
            : offer.offereeId === partyId
              ? offer.costs.settlement.offeree
              : undefined;
        for (const [ofType, amount] of side ? settlementCostsByType(side) : [])
          if (ofType === type) total += amount;
      }
      return total;
    },
    [locked, budget, settlements, partyId],
  );
  if (budget === undefined) {
    return (
      <Typography>
        {t("noBudget", { keyPrefix: "case.budget", name: partyName })}
      </Typography>
    );
  }

  // the barrister's base, and what of it reaches the law firm's base where
  // the firm engaged the barrister under a CFA its own covers (cfa.md #9):
  // with the barrister's success fee at its highest step, as if that CFA
  // succeeds
  const barristerFees = costsCovered("barristerFee", barristerCfa);
  const firmPaysBarrister =
    lawFirmCfa?.active === true &&
    lawFirmCfa.includedDisbursements.includes("barristerFee") &&
    barristerCfa?.active === true &&
    barristerCfa.engagedBy === "lawFirm" &&
    barristerCfaApplies(agreements);
  const barristerSuccessFee =
    firmPaysBarrister && barristerCfa
      ? (barristerFees -
          barristerFees * (barristerCfa.lowFee?.discountPercentage ?? 0)) *
        Math.max(
          0,
          ...stepsOf(barristerCfa.successFee).map(
            ({ successFeePercentage }) => successFeePercentage,
          ),
        )
      : 0;
  const barristerFeesOfFirm = barristerFees + barristerSuccessFee;
  const lawFirmBaseCost = (lawFirmCfa?.includedDisbursements ?? []).reduce(
    (total, type) =>
      total +
      (type === "barristerFee"
        ? barristerFeesOfFirm
        : costsCovered(type, lawFirmCfa)),
    costsCovered("lawFirmFee", lawFirmCfa),
  );
  // while the party has claims left, the base is its whole budget
  const ongoing = !isPartySettled(
    claims ?? [],
    parties ?? [],
    partyId,
    settlements,
  );
  const lawFirmNotes = [
    ...(ongoing ? [t("conditionalFeeAgreement.configurator.coveredBaseCosts.ongoing")] : []),
    ...(firmPaysBarrister
      ? [t("conditionalFeeAgreement.configurator.coveredBaseCosts.barristerSuccess")]
      : []),
  ];

  return (
    <Stack spacing={theme.spacing(2)} sx={{ mb: theme.spacing(2) }}>
      <Typography component="h1" gutterBottom>
        {t("mainTitle", { partyName })}
      </Typography>
      {/* Conditional Fee Agreement for law firm */}
      {lawFirmCfa && (
        <ConditionalFeeAgreementConfigurator
          label={t("agreements.lawFirmConditionalFeeAgreement")}
          agreement={lawFirmCfa}
          agreements={agreements}
          savedAgreement={saved?.lawFirmCfa}
          baseCost={lawFirmBaseCost}
          barristerFees={firmPaysBarrister ? barristerFeesOfFirm : barristerFees}
          baseCostNotes={lawFirmNotes}
          onSave={(lawFirmCfa) => onSave({ ...agreements, lawFirmCfa })}
          onUpdate={(lawFirmCfa) => onUpdate({ ...agreements, lawFirmCfa })}
          saveOngoing={saveOngoing}
        />
      )}
      {/* Conditional Fee Agreement for barristers */}
      {barristerCfa && (
        <ConditionalFeeAgreementConfigurator
          label={t("agreements.barristerConditionalFeeAgreement")}
          agreement={barristerCfa}
          agreements={agreements}
          savedAgreement={saved?.barristerCfa}
          baseCost={barristerFees}
          barristerFees={barristerFees}
          onSave={(barristerCfa) => onSave({ ...agreements, barristerCfa })}
          onUpdate={(barristerCfa) => onUpdate({ ...agreements, barristerCfa })}
          saveOngoing={saveOngoing}
        />
      )}
      {/* Third Party Funding Agreement */}
      {thirdPartyFunding && (
        <AgreementRow
          label={t("agreements.thirdPartyFunding")}
          active={false}
          onActiveChange={
            () => console.log("Not yet implemented")
            // onUpdate({
            //   ...agreements,
            //   thirdPartyFunding: { ...thirdPartyFunding, active },
            // })
          }
        >
          <ThirdPartyFundingAgreement />
        </AgreementRow>
      )}
      {/* After The Event Insurance Agreement */}
      {afterTheEventInsurance && (
        <AgreementRow
          label={t("agreements.afterTheEventInsurance")}
          active={false}
          onActiveChange={
            () => console.log("Not yet implemented")
            // onUpdate({
            //   ...agreements,
            //   afterTheEventInsurance: { ...afterTheEventInsurance, active },
            // })
          }
        >
          <AfterTheEventInsuranceAgreement />
        </AgreementRow>
      )}
    </Stack>
  );
};
